import { useQuery } from '@tanstack/react-query'
import { technicianAgenda } from '@kronos/domain'
import type { Instant } from '@kronos/domain'
import { repository } from '../../data'
import type { MySchedule } from '../../data/schedule'
import { problemOf } from '../hours/time'
import { EmptyState, PageHeader, Panel, Section } from '../primitives'
import { AnswerRow } from './AnswerRow'
import { AvailabilityEditor } from './AvailabilityEditor'
import { BookedRow } from './BookedRow'
import { MINE_KEY } from './shared'

/**
 * My schedule (/my/schedule): what the team has asked, what is booked, and
 * when I can come in. In that order, because an unanswered request is the one
 * thing on the page somebody else is waiting on.
 */
export function ScheduleView(): JSX.Element {
  const mine = useQuery({ queryKey: MINE_KEY, queryFn: () => repository.mySchedule() })

  return (
    <div className="page page--narrow schedule-page">
      <PageHeader title="My schedule" subtitle="Say when you can come in. When the team asks for a day, it shows up here." />
      {mine.isPending ? <p className="muted">Loading…</p> : null}
      {mine.isError ? <p className="critical">{problemOf(mine.error)}</p> : null}
      {mine.data ? <Body data={mine.data} /> : null}
    </div>
  )
}

function Body({ data }: { data: MySchedule }): JSX.Element {
  const { timeZone, today } = data
  const { needsAnswer, comingUp } = technicianAgenda(data.requests, data.now as Instant)

  return (
    <>
      {needsAnswer.length > 0 ? (
        <Section label={`Needs your answer · ${needsAnswer.length}`}>
          <Panel>
            {needsAnswer.map((r) => (
              <AnswerRow key={r.id} request={r} timeZone={timeZone} today={today} />
            ))}
          </Panel>
        </Section>
      ) : null}

      <Section label="Coming up">
        <Panel>
          {comingUp.length === 0 ? (
            <EmptyState title="Nothing booked yet">When you accept a request it shows here.</EmptyState>
          ) : (
            <div className="row-list">
              {comingUp.map((r) => (
                <BookedRow key={r.id} request={r} timeZone={timeZone} today={today} now={data.now} />
              ))}
            </div>
          )}
        </Panel>
      </Section>

      <AvailabilityEditor today={today} mine={data.availability} requests={data.requests} timeZone={timeZone} />
    </>
  )
}
